import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Table,
  Button,
  Badge,
  Input,
  Label,
  FormGroup,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

const todayISO = () => new Date().toISOString().split("T")[0];
const num = (v) => Number(v || 0);
const money = (v) =>
  `₹${num(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Admin repayment lookup. Shows the FIP (emi_schedule) or PIF (emi_schedule_pif) schedule via a
// toggle. Defaults to today's dues; a customer/loan search returns all of that entity's EMIs. Each
// row with an outstanding amount can be paid via a generated payment link.
const RepaymentSchedule = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [scheduleType, setScheduleType] = useState("fip"); // fip | pif
  const [filterType, setFilterType] = useState("customerId"); // customerId | loanId
  const [filterValue, setFilterValue] = useState("");
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(todayISO());

  // Pay modal
  const [modalOpen, setModalOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [payCustomerId, setPayCustomerId] = useState(null);
  const userId = localStorage.getItem("user_id");

  // Client-side pagination (all rows are loaded for the chosen filters).
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  useEffect(() => { setPage(0); }, [rows]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const fetchData = useCallback(async (override = {}) => {
    setLoading(true);
    setError("");
    try {
      const type = override.type ?? scheduleType;
      const fType = override.filterType ?? filterType;
      const fVal = override.filterValue ?? filterValue;
      const from = override.from ?? fromDate;
      const to = override.to ?? toDate;

      const params = { type };
      if (fVal) {
        // A specific customer/loan search returns all their EMIs (not constrained to a date range).
        params[fType] = fVal;
      } else {
        if (from) params.fromDate = from;
        if (to) params.toDate = to;
      }

      const res = await axiosInstance.get(URL.REPORTING_EMI, { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to fetch repayments:", e);
      setError("Could not load repayments data.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [scheduleType, filterType, filterValue, fromDate, toDate]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleReset = () => {
    setFilterValue("");
    setFromDate(todayISO());
    setToDate(todayISO());
    fetchData({ filterValue: "", from: todayISO(), to: todayISO() });
  };

  const onToggleType = (t) => {
    setScheduleType(t);
    fetchData({ type: t });
  };

  const openPayModal = (row) => {
    setAmount(num(row.outstanding_amount).toFixed(2));
    setPayCustomerId(row.customer_id);
    setPaymentLink("");
    setPayError("");
    setModalOpen(true);
  };

  const [payError, setPayError] = useState("");

  const generatePaymentLink = async () => {
    setPayError("");
    if (!userId) {
      setPayError("You are not logged in (missing user id). Please re-login.");
      return;
    }
    try {
      const res = await axiosInstance.get(
        `${URL.PAYMENT_LINK_GENERATE}?customer_id=${payCustomerId}&user_id=${userId}&amount=${amount}&flags=4`
      );
      const link = res.data?.payInResponse?.link;
      if (res.data?.payInResponse?.success && link) {
        setPaymentLink(link);
      } else {
        // Surface the backend/gateway reason instead of failing silently.
        setPayError(res.data?.error || "Payment gateway did not return a link.");
      }
    } catch (e) {
      console.error("Error generating payment link:", e);
      setPayError(e.response?.data?.error || e.message || "Payment link generation failed.");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(paymentLink);
    alert("Payment link copied!");
  };

  // Summary metrics for the currently filtered rows (follow the date / customer / loan filter). Demand
  // & collection are on a principal+interest basis: penal is excluded, since it can push
  // outstanding_amount above emi_amount and make a naive "emi - outstanding" go negative.
  const demandOf = (r) => {
    const pi = num(r.principal) + num(r.interest);
    return pi > 0 ? pi : num(r.emi_amount);
  };
  const collectedOf = (r) => {
    const demand = demandOf(r);
    const owed = (r.outstanding_principal != null || r.outstanding_interest != null)
      ? num(r.outstanding_principal) + num(r.outstanding_interest)
      : Math.min(num(r.outstanding_amount), demand);
    return Math.max(0, Math.min(demand, demand - owed));
  };
  const emiCount = rows.length;
  const totalDemand = rows.reduce((s, r) => s + demandOf(r), 0);
  const totalCollection = rows.reduce((s, r) => s + collectedOf(r), 0);
  const repaymentRate = totalDemand > 0 ? (totalCollection / totalDemand) * 100 : 0;

  const Metric = ({ label, value, color }) => (
    <Col xs="6" md="3" className="mb-2 mb-md-0">
      <div className="text-uppercase text-muted small font-weight-bold">{label}</div>
      <div className="h3 mb-0 font-weight-bold" style={{ color }}>{value}</div>
    </Col>
  );

  return (
    <Container className="mt--7" fluid>
      <Card className="bg-gradient-default shadow mb-4">
        <CardBody>
          <h6 className="text-uppercase text-light ls-1 mb-1">Salora Capital · NBFC</h6>
          <h2 className="text-white mb-0">Repayments</h2>
        </CardBody>
      </Card>
      <Row>
        <Col xl="12">
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col xs="6">
                  <h3 className="mb-0">
                    Repayments{" "}
                    <Badge color="secondary" pill className="ml-2">{rows.length}</Badge>
                  </h3>
                </Col>
                <Col xs="6" className="text-right">
                  <Button color="primary" size="sm" onClick={() => fetchData()} disabled={loading}>
                    {loading ? "Loading…" : "Refresh"}
                  </Button>
                </Col>
              </Row>
              <Form onSubmit={handleSearch} className="mt-3">
          <div className="d-flex align-items-end filter-bar" style={{ gap: "12px" }}>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Schedule</Label>
              <Input type="select" bsSize="sm" value={scheduleType}
                onChange={(e) => onToggleType(e.target.value)}>
                <option value="fip">FIP</option>
                <option value="pif">PIF</option>
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Search by</Label>
              <Input type="select" bsSize="sm" value={filterType}
                onChange={(e) => setFilterType(e.target.value)}>
                <option value="customerId">Customer ID</option>
                <option value="loanId">Loan ID</option>
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Value</Label>
              <Input type="text" bsSize="sm" placeholder="Enter id..." value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">From Date</Label>
              <Input type="date" bsSize="sm" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">To Date</Label>
              <Input type="date" bsSize="sm" value={toDate}
                onChange={(e) => setToDate(e.target.value)} />
            </FormGroup>
            <Button color="primary" size="sm" type="submit" disabled={loading}>Search</Button>
            <Button color="secondary" size="sm" type="button" onClick={handleReset} disabled={loading}>Reset</Button>
          </div>
        </Form>

        {!loading && !error && rows.length > 0 && (
          <Row className="mt-3 pt-3 border-top align-items-center">
            <Metric label="Count (EMIs)" value={emiCount} color="#525f7f" />
            <Metric label="Total Demand" value={money(totalDemand)} color="#123a7a" />
            <Metric label="Total Collection" value={money(totalCollection)} color="#2e7d32" />
            <Metric label="Repayment Rate" value={`${repaymentRate.toFixed(2)}%`} color="#1e4fbf" />
          </Row>
        )}
      </CardHeader>

      {loading && <CardBody className="text-center text-muted py-5">Loading repayments…</CardBody>}
      {!loading && error && <CardBody className="text-center text-danger py-5">{error}</CardBody>}
      {!loading && !error && rows.length === 0 && (
        <CardBody className="text-center text-muted py-5">No repayments records found for the selected filters.</CardBody>
      )}
      {!loading && !error && rows.length > 0 && (
        <div className="table-scroll">
        <Table className="align-items-center table-flush">
          <thead className="thead-light">
            <tr>
              <th scope="col">Loan Code</th>
              <th scope="col">Customer Code</th>
              <th scope="col">Due Date</th>
              <th scope="col">EMI Amount</th>
              <th scope="col">Principal</th>
              <th scope="col">Interest</th>
              <th scope="col">Penal Interest</th>
              <th scope="col">DPD</th>
              <th scope="col">EMI Status</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr key={row.id}>
                <td>{row.loan_code || row.loan_id}</td>
                <td>{row.customer_code || row.customer_id}</td>
                <td>{row.emi_date}</td>
                <td>{money(row.emi_amount)}</td>
                <td>{money(row.principal)}</td>
                <td>{money(row.interest)}</td>
                <td>{money(row.penal_interest)}</td>
                <td>{row.dpd}</td>
                <td>{row.emi_status_value}</td>
                <td>
                  {num(row.outstanding_amount) > 0 ? (
                    <Button color="success" size="sm" onClick={() => openPayModal(row)}>Pay</Button>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        </div>
      )}
      {!loading && !error && rows.length > 0 && (
        <div className="d-flex justify-content-between align-items-center px-3 py-3">
          <Button color="secondary" size="sm" outline disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}>
            <i className="fa fa-chevron-left mr-1" /> Prev
          </Button>
          <span className="text-muted small">Page {page + 1} of {totalPages}</span>
          <Button color="secondary" size="sm" outline disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
            Next <i className="fa fa-chevron-right ml-1" />
          </Button>
        </div>
      )}

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>Generate Payment Link</ModalHeader>
        <ModalBody>
          <Label>Amount</Label>
          <Input type="text" value={amount} onChange={(e) => setAmount(e.target.value)} />
          {paymentLink && (
            <>
              <Label className="mt-3">Payment Link</Label>
              <Input value={paymentLink} disabled />
            </>
          )}
          {payError && <div className="text-danger mt-3 small">{payError}</div>}
        </ModalBody>
        <ModalFooter>
          {!paymentLink ? (
            <Button color="primary" onClick={generatePaymentLink}>Generate Link</Button>
          ) : (
            <Button color="secondary" onClick={copyLink}>Copy Link</Button>
          )}
          <Button color="danger" onClick={() => setModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RepaymentSchedule;
